import { BoolFilter, FilterList, SelectFilter, useQsFilters } from "@dash/filters";
import { Chip } from "@dash/form";
import { SearchWithPagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import React from "react";

import { FulfillmentDto } from "@megaron/loyalty-orders-contracts";
import { rewardIds } from "@megaron/loyalty-orders-rewards";
import { useClientManager } from "@megaron/react-clients";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { BlikCheckStatusChip } from "./BlikCheckStatusChip";
import { OrdersTable } from "./OrdersTable";
import { ShoperStatusChip } from "./ShoperStatusChip";

const filteredRewardIds = rewardIds.filter((r) => !r.toLowerCase().includes("testreward"));

const pageSize = 30;

const filtersSerializer = Serializers.object({
  page: Serializers.integer,
  rewardId: Serializers.array(Serializers.string),
  isFulfilled: Serializers.boolean.optional(),
  userUuid: uuidSerializer.optional(),
  searchTerm: Serializers.string.optional(),
});

export type Filters = SerializerValue<typeof filtersSerializer>;

export const OrderList: React.FunctionComponent = () => {
  const { filters, setFilter } = useQsFilters(filtersSerializer, {
    page: 0,
    rewardId: [],
    isFulfilled: undefined,
    userUuid: undefined,
    searchTerm: "",
  });

  const query = useClientManager("loyaltyOrders")
    .orders()
    .useQuery({
      userUuid: filters.userUuid,
      rewardId: filters.rewardId.length > 0 ? filters.rewardId : undefined,
      isFulfilled: filters.isFulfilled,
      limit: pageSize,
      offset: filters.page * pageSize,
      searchTerm: filters.searchTerm,
    });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
      <SearchWithPagination
        searchValue={filters.searchTerm ?? ""}
        onSearchChange={setFilter("searchTerm")}
        itemCount={query.data?.count}
        pageSize={pageSize}
        page={filters.page}
        onPageChange={setFilter("page")}
      />
      <FilterList>
        <SelectFilter
          label="Nagroda"
          initiallySelectedValues={filters.rewardId}
          options={filteredRewardIds.map((r) => ({ label: r, value: r }))}
          onSelectedChange={(options) => setFilter("rewardId")(options.map((o) => o.value))}
          variant="multi-select"
        />
        <BoolFilter
          label="Zrealizowane?"
          value={filters.isFulfilled}
          falseLabel="Nie zrealizowane"
          trueLabel="Zrealizowane"
          onChange={setFilter("isFulfilled")}
        />
      </FilterList>
      <QuerySkeleton query={query}>
        {(page) => (
          <OrdersTable
            orders={page.items}
            onRewardClick={(v) => setFilter("rewardId")([v])}
            onUserClick={setFilter("userUuid")}
          />
        )}
      </QuerySkeleton>
    </div>
  );
};

export const FulfillmentChip: React.FunctionComponent<{
  fulfillment: FulfillmentDto;
}> = (props) => {
  const theme = useTheme();

  if (props.fulfillment.type === "blikCheck") {
    return <BlikCheckStatusChip status={props.fulfillment.status} isLabeled />;
  }
  if (props.fulfillment.type === "custom")
    return props.fulfillment.isFailed ? (
      <Chip color={theme.colors.danger}>Nieudane</Chip>
    ) : props.fulfillment.isFulfilled ? (
      <Chip>Zrealizowane</Chip>
    ) : (
      <Chip variant="secondary">Oczekuje</Chip>
    );
  if (props.fulfillment.type === "shoper") return <ShoperStatusChip status={props.fulfillment.status} isLabeled />;
  return null;
};
